import { useEffect } from "react";
import { pickLocale } from "../../lib/format";

/**
 * `jsonLd` — extra schema.org nodes to publish alongside whatever the editor
 * wrote into `seo.structured_data`.
 *
 * The PDP emitted no `Product` node at all. `buildJsonLd` only ever republished
 * a hand-authored `structured_data` blob and an optional FAQ, so unless someone
 * typed schema.org JSON into a CMS field by hand, a product page carried no
 * price, no availability, no brand and no SKU — nothing Google needs for a rich
 * result or a Shopping listing. The backend has built exactly the right payload
 * this whole time at `GET /api/seo/product/{handle}/jsonld`, with availability
 * read from live inventory; nothing called it.
 */
export default function SeoMeta({ title, description, seo = {}, locale = "az", canonicalPath, image, type = "website", publishedAt, modifiedAt, jsonLd }) {
  const resolvedTitle = pickLocale(title, locale) || String(title || "Atelier Di Falco");
  // `pickLocale` indexes a dict; handed a plain string it returns "". The title
  // already guarded against that, the description did not — so any caller
  // passing a ready-made string silently shipped an empty meta description.
  const resolvedDescription = pickLocale(description, locale)
    || (typeof description === "string" ? description : "");
  const resolvedOgTitle = pickLocale(seo.og_title, locale) || resolvedTitle;
  const resolvedOgDescription = pickLocale(seo.og_description, locale) || resolvedDescription;

  useEffect(() => {
    document.title = resolvedTitle
      ? (resolvedTitle.toLocaleLowerCase().includes("atelier di falco") ? resolvedTitle : `${resolvedTitle} | Atelier Di Falco`)
      : "Atelier Di Falco";
    setMeta("description", resolvedDescription);
    setMeta("keywords", Array.isArray(seo.keywords) ? seo.keywords.join(", ") : "");
    setMeta("robots", seo.index === false ? "noindex, follow" : "index, follow");
    setMeta("author", seo.author || "");
    setProperty("og:type", type);
    setProperty("og:title", resolvedOgTitle);
    setProperty("og:description", resolvedOgDescription);
    setProperty("og:image", seo.og_image || image || "");
    setProperty("article:published_time", type === "article" ? publishedAt || "" : "");
    setProperty("article:modified_time", type === "article" ? modifiedAt || "" : "");

    const canonical = seo.canonical_url || (canonicalPath ? `${window.location.origin}${canonicalPath}` : "");
    setCanonical(canonical);
    setJsonLd(buildJsonLd(seo, locale, jsonLd));
  }, [canonicalPath, image, jsonLd, locale, modifiedAt, publishedAt, resolvedDescription, resolvedOgDescription, resolvedOgTitle, resolvedTitle, seo, type]);

  return null;
}

function buildJsonLd(seo, locale, extra) {
  const nodes = [];
  // Server-built nodes first: Product, Offer, BreadcrumbList. An editor's
  // hand-written `structured_data` still follows and can add to them.
  (Array.isArray(extra) ? extra : [extra]).forEach((node) => {
    if (node && typeof node === "object") nodes.push(node);
  });
  if (seo.structured_data && typeof seo.structured_data === "object" && !Array.isArray(seo.structured_data)) {
    nodes.push(seo.structured_data);
  }
  if (Array.isArray(seo.faq) && seo.faq.length) {
    nodes.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: seo.faq.map((item) => ({
        "@type": "Question",
        name: pickLocale(item?.question, locale) || String(item?.question || ""),
        acceptedAnswer: {
          "@type": "Answer",
          text: pickLocale(item?.answer, locale) || String(item?.answer || ""),
        },
      })),
    });
  }
  if (!nodes.length) return null;
  if (nodes.length === 1) return nodes[0];
  return { "@context": "https://schema.org", "@graph": nodes };
}

function setMeta(name, content) {
  let element = document.head.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content || "");
}

function setProperty(property, content) {
  let element = document.head.querySelector(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content || "");
}

function setCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", href || window.location.href);
}

function setJsonLd(payload) {
  let element = document.head.querySelector('script[data-storefront-jsonld="true"]');
  if (!payload) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("script");
    element.setAttribute("type", "application/ld+json");
    element.setAttribute("data-storefront-jsonld", "true");
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(payload);
}
